import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React ErrorBoundary class component — catches render errors, displays fallback UI.
 * Telemetry: AppInsights.trackException w componentDidCatch (gdy AI initialized).
 */
export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // T-Faza6 wires AI.trackException tutaj.
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  public override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div role="alert" className="flex min-h-dvh items-center justify-center p-8">
          <div className="max-w-lg rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] p-8 shadow-sm">
            <h1 className="mb-2 text-xl font-bold text-[var(--color-danger)]">
              Coś poszło nie tak
            </h1>
            <p className="text-sm text-[var(--color-text-secondary)]">
              {this.state.error?.message ?? 'Nieoczekiwany błąd aplikacji.'}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 rounded-md bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-[var(--color-accent-foreground)] hover:bg-[var(--color-accent-hover)]"
            >
              Odśwież
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
